// WalletConnect v2 (Reown) — connect Qastle Wallet (or any WC wallet) on KROWN Network.
// Returns a standard EIP-1193 provider so it plugs straight into the existing personal_sign /
// eth_sendTransaction flows (mirrors the `connectWallet` signer shape in walletSign.js).
import { EthereumProvider } from "@walletconnect/ethereum-provider";
import { NETWORKS } from "@/lib/cryptoSend";

const PROJECT_ID = "40141c45273e554b956ba15320fcc174";
const KROWN = NETWORKS.krown; // EVM L1, chainId 1983
const BASE_SEPOLIA = NETWORKS.baseSepolia; // testnet used by in-chat crypto sends

let providerPromise = null;

// Lazily initialize a single WalletConnect provider (built-in dark QR modal).
export function getWcProvider() {
  if (!providerPromise) {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://bloqtalk.app";
    providerPromise = EthereumProvider.init({
      projectId: PROJECT_ID,
      chains: [KROWN.chainId],
      // KROWN (1983) is listed in BOTH required + optional namespaces so the approved session
      // always advertises eip155:1983 — Qastle builds its AutoNamespaces from optionalChains, and
      // without this personal_sign failed with "Missing or invalid. request() chainId: eip155:1983".
      optionalChains: [KROWN.chainId, BASE_SEPOLIA.chainId],
      rpcMap: {
        [KROWN.chainId]: KROWN.rpc,
        [BASE_SEPOLIA.chainId]: BASE_SEPOLIA.rpc,
      },
      methods: [
        "personal_sign",
        "eth_sendTransaction",
        "eth_signTypedData",
        "eth_signTypedData_v4",
        "wallet_switchEthereumChain",
        "wallet_addEthereumChain",
      ],
      events: ["chainChanged", "accountsChanged"],
      showQrModal: true,
      qrModalOptions: { themeMode: "dark" },
      metadata: {
        name: "BLOQ Talk",
        description: "Trust-gated Web3 messenger — connect Qastle Wallet on KROWN Network.",
        url: origin,
        icons: [`${origin}/icon-192.png`],
      },
    });
  }
  return providerPromise;
}

// Connect via WalletConnect (opens the built-in dark modal) and return a signer that matches
// the shape returned by connectWallet(): { address, family, sign(message), provider, disconnect }.
export async function connectViaWalletConnect() {
  const provider = await getWcProvider();
  let accounts = provider.accounts || [];
  if (!accounts.length) accounts = await provider.enable();
  const address = accounts?.[0];
  if (!address) throw new Error("No account selected");
  return {
    address,
    family: "evm",
    provider,
    sign: (message) => provider.request({ method: "personal_sign", params: [message, address] }),
    disconnect: () => resetWalletConnect(),
  };
}

// Tear down the session so the next connect starts fresh. Full teardown (disconnect + drop all
// listeners + reset the singleton + clear stale wc@2 cache) so reconnecting Qastle never returns a
// stale/old address from a cached session.
export async function resetWalletConnect() {
  const pending = providerPromise;
  providerPromise = null; // reset the singleton first → next getWcProvider() re-inits clean
  if (pending) {
    try {
      const provider = await pending;
      provider.removeAllListeners?.();
      await provider.disconnect();
    } catch {
      /* ignore */
    }
  }
  // Belt-and-suspenders: wipe any lingering WalletConnect v2 session/pairing cache so a reconnect
  // negotiates a brand-new session (defeats the "different address on reconnect" bug).
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      Object.keys(window.localStorage)
        .filter((k) => k.startsWith("wc@2"))
        .forEach((k) => window.localStorage.removeItem(k));
    }
  } catch {
    /* ignore */
  }
}
